import { useCallback } from 'react'
import keys from 'constants/keys'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { useAILoadingContext } from 'components/TasksV2/AITaskCreation/AIFormStepContext'
import { IAIGeneratedSubtask } from 'components/TasksV2/AITaskCreation/hooks/useAISubtaskGeneration'
import type { IAIPromptStepData } from 'components/TasksV2/AITaskCreation/hooks/useAiPromptForm'

import {
  AI_PROMPT_SLICE,
  AI_SUBTASK_DRAFT_SLICE,
  buildAiSubtaskDraft,
  IAiPromptDraft,
  IAiSubtaskSeed,
} from '../model/subtaskDraft'

export { AI_PROMPT_SLICE, AI_SUBTASK_DRAFT_SLICE, buildAiSubtaskDraft } from '../model/subtaskDraft'
export type { IAiPromptDraft, IAiSubtaskSeed } from '../model/subtaskDraft'

/**
 * The parts of the "enhance one subtask with AI" flow that do not depend on WHERE the subtask lives:
 * seeding the two scratch slices, persisting the prompt, kicking off generation and reading the result.
 *
 * `TaskCreation/hooks/useVmAiEnhance` hosts this for flat and grouped VM builders,
 * with the subtask in `SUBTASK_TASK_CREATION.sub_tasks[i]`.
 *
 * Must be called under `FormStepProvider`, and under `AIGenerationProvider` for `startGeneration`.
 */
export const useAiSubtaskDraft = () => {
  const { formData, getFormData, updateFormData } = useFormStepContext()
  const { generateFromPrompt } = useAILoadingContext()

  const readFormData = useCallback(() => (getFormData?.() ?? formData) as any, [getFormData, formData])

  /** Fills both slices from the subtask's current content — call right before entering the flow. */
  const seed = useCallback(
    ({ prompt, referenceImages, title, richText, images }: IAiSubtaskSeed) => {
      updateFormData(
        AI_PROMPT_SLICE as any,
        {
          prompt: prompt ?? '',
          reference_images: referenceImages ?? [],
        } as any
      )
      updateFormData(
        AI_SUBTASK_DRAFT_SLICE as any,
        {
          title: '',
          sub_tasks: [
            {
              title,
              type: keys.SUBTASK_TYPES.IMAGE_SUBTASK,
              order_index: 0,
              is_required: true,
              content: { rich_text: richText ?? null, images: images ?? [] },
            },
          ],
        } as any
      )
    },
    [updateFormData]
  )

  const readPrompt = useCallback((): IAiPromptDraft => {
    const slice = readFormData()[AI_PROMPT_SLICE] ?? {}
    return { prompt: slice.prompt ?? '', reference_images: slice.reference_images ?? [] }
  }, [readFormData])

  const savePrompt = useCallback(
    (data: IAIPromptStepData) => {
      updateFormData(AI_PROMPT_SLICE as any, { ...readFormData()[AI_PROMPT_SLICE], ...data } as any)
    },
    [readFormData, updateFormData]
  )

  /**
   * Not awaited by design: the subtask step renders its loader while this resolves. `scopeKey` keeps the
   * skip-if-unchanged check per subtask, so the same prompt text can be reused on another subtask.
   */
  const startGeneration = useCallback(
    (data: IAIPromptStepData, scopeKey: string) => {
      generateFromPrompt(data.prompt, data.reference_images || [], scopeKey)
    },
    [generateFromPrompt]
  )

  /** Stores the LLM result into the scratch slice — passed to `AIGenerationProvider`. */
  const writeDraftFromLLM = useCallback(
    (result: IAIGeneratedSubtask, referenceImages: string[]) => {
      updateFormData(AI_SUBTASK_DRAFT_SLICE as any, buildAiSubtaskDraft(result, referenceImages) as any)
    },
    [updateFormData]
  )

  return { seed, readPrompt, savePrompt, startGeneration, writeDraftFromLLM }
}
